import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AllocationsRequest,
  divisionProblem,
  formatMoney,
  fromCents,
  type InvoiceJobsResponse,
  type InvoiceExclusionRequest,
  proposedItem,
  toCents,
} from '@k-management/shared';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { moneyOf } from '../people/people-ui.js';
import { jobKeys } from './jobs-ui.js';

const invoiceJobsKey = (invoiceId: string) => [...jobKeys.all, 'invoice', invoiceId] as const;

type Data = InvoiceJobsResponse;

/** «riga 2 · Spese di trasferta»: a line as the division names it. */
function lineLabel(data: Data, position: number): string {
  const line = data.lines.find((item) => item.position === position);
  return line ? `riga ${position + 1} · ${line.description}` : `riga ${position + 1}`;
}

/**
 * The division of an invoice among commesse (2.03, 17/10/2026): it is managed
 * here, on the invoice, where its whole imponibile is in view; the commessa
 * shows its part. Since 2.44 (27/10/2026) it goes by line: the whole invoice to
 * one commessa, each line to its own, or a line among several.
 */
export function InvoiceJobsCard({ invoiceId }: { invoiceId: string }) {
  const canJobs = useCan('commesse.read');
  const [editing, setEditing] = useState(false);
  const [excluding, setExcluding] = useState(false);
  const queryClient = useQueryClient();
  const include = useMutation({
    mutationFn: () =>
      apiRequest<InvoiceJobsResponse>(`/jobs/invoices/${invoiceId}/exclusion`, {
        method: 'DELETE',
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(invoiceJobsKey(invoiceId), saved);
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
  const division = useQuery({
    queryKey: invoiceJobsKey(invoiceId),
    queryFn: () => apiRequest<InvoiceJobsResponse>(`/jobs/invoices/${invoiceId}`),
  });
  if (!division.data) return null;
  const data = division.data;
  const free = toCents(data.taxable) - toCents(data.allocated);
  const toReview = data.allocations.some((row) => row.toReview);

  return (
    <div className="card stack">
      <div className="row between">
        <h3>Commesse e progetti</h3>
        <div className="row">
          {data.excluded && <span className="pill">Non riguarda commesse</span>}
          {data.allocations.length > 0 &&
            (free === 0 ? (
              <span className="pill ok">Tutto imputato</span>
            ) : (
              <span className="pill warn">Da imputare {formatMoney(fromCents(free))}</span>
            ))}
          {data.canEdit && data.allocations.length === 0 && !data.excluded && (
            <button type="button" onClick={() => setExcluding(true)}>
              Non riguarda commesse…
            </button>
          )}
          {data.canEdit && (
            <button type="button" onClick={() => setEditing(true)}>
              {data.allocations.length ? 'Modifica ripartizione' : 'Imputa…'}
            </button>
          )}
        </div>
      </div>
      {data.excluded ? (
        <div className="row between">
          <p className="muted">
            Segnata da {data.excluded.by ?? 'un utente'} il{' '}
            {new Date(data.excluded.at).toLocaleDateString('it-IT')}: non va su nessuna commessa e
            non compare tra quelle da imputare.
            {data.excluded.note && <> Nota: {data.excluded.note}</>}
          </p>
          {data.canEdit && (
            <button
              type="button"
              className="link"
              disabled={include.isPending}
              onClick={() => include.mutate()}
            >
              Togli il segno
            </button>
          )}
        </div>
      ) : data.allocations.length === 0 ? (
        <p className="muted">Non è imputata a nessuna commessa o progetto.</p>
      ) : (
        <table className="lines">
          <tbody>
            {data.allocations.map((row) => (
              <tr key={row.id}>
                {data.lines.length > 0 && (
                  <td className="muted">
                    {row.linePosition === null
                      ? 'intera fattura'
                      : lineLabel(data, row.linePosition)}
                    {row.toReview && (
                      <>
                        {' '}
                        <span className="pill warn">da rivedere</span>
                      </>
                    )}
                  </td>
                )}
                <td>
                  {canJobs ? (
                    <Link to={`/commesse/${row.jobId}`}>
                      {row.jobCode} · {row.jobName}
                    </Link>
                  ) : (
                    `${row.jobCode} · ${row.jobName}`
                  )}
                </td>
                <td className="muted">
                  {data.direction === 'emessa'
                    ? row.installment
                      ? `rata «${row.installment}»`
                      : 'senza rata'
                    : row.cost
                      ? `costo «${row.cost}»`
                      : 'senza costo'}
                </td>
                <td className="right">{formatMoney(row.amount)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={data.lines.length > 0 ? 3 : 2} className="muted">
                Imputato · imponibile della fattura {formatMoney(data.taxable)}
              </td>
              <td className="right">
                <strong>{formatMoney(data.allocated)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {toReview && (
        <p className="notice info">
          Una riga è cambiata in Gestify dopo l’imputazione: rivedi la ripartizione e salvala di
          nuovo.
        </p>
      )}
      {include.isError && <p className="error">{include.error.message}</p>}
      {editing && (
        <AllocationsDialog invoiceId={invoiceId} data={data} onClose={() => setEditing(false)} />
      )}
      {excluding && <ExclusionDialog invoiceId={invoiceId} onClose={() => setExcluding(false)} />}
    </div>
  );
}

/** «Non riguarda commesse» (2.47): the invoice leaves the ones to allocate, with a note. */
function ExclusionDialog({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: (body: InvoiceExclusionRequest) =>
      apiRequest<InvoiceJobsResponse>(`/jobs/invoices/${invoiceId}/exclusion`, {
        method: 'PUT',
        body,
      }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(invoiceJobsKey(invoiceId), saved);
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onClose();
    },
  });
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Non riguarda commesse</h2>
        <p className="muted small">
          La fattura non va su nessuna commessa o progetto (per esempio spese generali di un cliente
          che ha anche commesse): esce dal filtro «Da imputare» e dalle fatture proposte nella
          commessa. Se poi la imputi, il segno si toglie da solo.
        </p>
        <Field label="Nota" hint="Facoltativa: perché non riguarda le commesse.">
          <textarea
            value={note}
            maxLength={500}
            rows={3}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending}
            onClick={() => save.mutate({ note: note.trim() || null })}
          >
            {save.isPending ? 'Attendere…' : 'Segna'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** A received invoice on no cost, chosen on purpose: a spesa non prevista (2.28). */
/** «700,00 + cassa 28,00»: the share of the imponibile outside the lines that goes with a line (2.56). */
export function LineRest({ line }: { line: Data['lines'][number] }) {
  if (toCents(line.rest) === 0) return null;
  return (
    <small
      className="muted block"
      title="La cassa previdenziale è nell’imponibile ma non è una riga della fattura: segue le righe"
    >
      {formatMoney(line.base)} + cassa {formatMoney(line.rest)}
    </small>
  );
}

const NO_COST = 'none';

interface Part {
  key: number;
  /** The line it comes from, or null for a part of the whole invoice. */
  linePosition: number | null;
  /** Empty: the line is not given to anything. */
  jobId: string;
  /** The rata of an issued invoice, the cost of a received one. */
  itemId: string;
  amount: string;
}

let nextKey = 0;
const part = (fields: Omit<Part, 'key'>): Part => ({ key: nextKey++, ...fields });

/** The parts as saved; each line without one gets an empty part, to choose its commessa. */
function startingParts(data: Data): Part[] {
  const issued = data.direction === 'emessa';
  const saved = data.allocations.map((row) =>
    part({
      linePosition: row.linePosition,
      jobId: row.jobId,
      itemId: (issued ? row.installmentId : row.costId) ?? '',
      amount: row.amount,
    }),
  );
  if (!data.lines.length) {
    return saved.length
      ? saved
      : [part({ linePosition: null, jobId: '', itemId: '', amount: data.taxable })];
  }
  const empty = data.lines
    .filter((line) => !saved.some((item) => item.linePosition === line.position))
    .map((line) =>
      part({ linePosition: line.position, jobId: '', itemId: '', amount: line.amount }),
    );
  return [...saved, ...empty];
}

export function AllocationsDialog({
  invoiceId,
  data,
  jobId,
  onClose,
}: {
  invoiceId: string;
  data: Data;
  /** Opened from a commessa (2.56): the lines not yet given start on it, each with its proposal. */
  jobId?: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const issued = data.direction === 'emessa';
  const byLine = data.lines.length > 0;
  const [parts, setParts] = useState<Part[]>(() => {
    const starting = startingParts(data);
    if (!jobId || !data.candidates.some((job) => job.id === jobId)) return starting;
    const given: Part[] = [];
    for (const item of starting) {
      given.push(
        item.jobId
          ? item
          : { ...item, jobId, itemId: proposalFor(data, jobId, item.amount, given) },
      );
    }
    return given;
  });
  const [whole, setWhole] = useState({ jobId: '', itemId: '' });
  // «Senza rata» on a commessa with rate still to invoice: said once more (2.47).
  const [withoutRata, setWithoutRata] = useState(false);
  const save = useMutation({
    mutationFn: (body: AllocationsRequest) =>
      apiRequest<InvoiceJobsResponse>(`/jobs/invoices/${invoiceId}`, { method: 'PUT', body }),
    onSuccess: async (saved) => {
      queryClient.setQueryData(invoiceJobsKey(invoiceId), saved);
      await queryClient.invalidateQueries({ queryKey: jobKeys.all });
      onClose();
    },
  });

  const update = (key: number, change: Partial<Part>) =>
    setParts(
      parts.map((item) => {
        if (item.key !== key) return item;
        // A commessa just chosen comes with its rata or cost proposed (2.47), net of the other parts.
        const others = parts.filter((other) => other.key !== key);
        return change.jobId !== undefined && change.jobId !== item.jobId
          ? {
              ...item,
              ...change,
              itemId: proposalFor(data, change.jobId, moneyOf(item.amount) ?? '0', others),
            }
          : { ...item, ...change };
      }),
    );
  const remove = (key: number) => setParts(parts.filter((item) => item.key !== key));
  const given = parts.filter((item) => item.jobId);
  const amounts = new Map(given.map((item) => [item.key, moneyOf(item.amount)]));
  const unreadable = given.some((item) => amounts.get(item.key) === null);
  const problem = unreadable
    ? null
    : divisionProblem({
        taxable: data.taxable,
        lines: data.lines,
        parts: given.map((item) => ({
          linePosition: item.linePosition,
          amount: amounts.get(item.key)!,
        })),
      });
  const total = given.reduce((sum, item) => sum + toCents(amounts.get(item.key) ?? '0'), 0);
  const free = toCents(data.taxable) - total;
  // On a commessa with costs a received invoice names its cost, or says it has none (2.28).
  const costMissing = (item: Part) =>
    !issued &&
    Boolean(item.jobId) &&
    !item.itemId &&
    (data.candidates.find((job) => job.id === item.jobId)?.costs.length ?? 0) > 0;
  // An issued invoice left «senza rata» on a commessa with rate still to invoice (2.47).
  const rataLeft = (item: Part) =>
    issued && Boolean(item.jobId) && !item.itemId && openItems(data, item.jobId, given).length > 0;
  const withoutRataJobs = [
    ...new Set(given.filter(rataLeft).map((item) => jobCode(data, item.jobId))),
  ];
  const ready =
    !unreadable &&
    !problem &&
    !given.some(costMissing) &&
    (withoutRataJobs.length === 0 || withoutRata);

  /** The rest of a line, or of the invoice, not yet given. */
  const restOf = (linePosition: number | null) => {
    const of =
      linePosition === null
        ? data.taxable
        : (data.lines.find((line) => line.position === linePosition)?.amount ?? '0');
    const taken = given
      .filter((item) => item.linePosition === linePosition)
      .reduce((sum, item) => sum + toCents(amounts.get(item.key) ?? '0'), 0);
    return fromCents(toCents(of) - taken);
  };
  const addTo = (linePosition: number | null) =>
    setParts([
      ...parts,
      part({
        linePosition,
        jobId: '',
        itemId: '',
        // A line all given: the new part starts empty, and its share is written by hand.
        amount: toCents(restOf(linePosition)) === 0 ? '' : restOf(linePosition),
      }),
    ]);
  const applyWhole = () =>
    setParts(
      data.lines.map((line) =>
        part({
          linePosition: line.position,
          jobId: whole.jobId,
          itemId: whole.itemId,
          amount: line.amount,
        }),
      ),
    );

  const lineParts = (position: number) => parts.filter((item) => item.linePosition === position);
  const wholeParts = parts.filter((item) => item.linePosition === null);
  // Parts of lines Gestify no longer has: to remove before saving.
  const lostParts = parts.filter(
    (item) =>
      item.linePosition !== null && !data.lines.some((line) => line.position === item.linePosition),
  );

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog allocation-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Ripartizione su commesse e progetti</h2>
        <p className="muted small">
          Imponibile della fattura {formatMoney(data.taxable)}.{' '}
          {byLine
            ? 'Ogni riga va a una o più commesse; le parti di una riga non la superano. Ciò che resta libero è segnalato, ma si può salvare.'
            : 'La fattura non ha righe: si divide per importo, le parti non superano l’imponibile.'}{' '}
          {issued
            ? 'Prima le commesse del cliente o di «Si fattura a», con le loro rate.'
            : 'Prima le commesse con costi di questo fornitore, poi le altre e i progetti.'}{' '}
          Le commesse con sottocommesse non compaiono: si imputa alle figlie.
        </p>

        {byLine && (
          <div className="allocation-whole">
            <strong>Tutta la fattura a</strong>
            <JobSelect
              data={data}
              value={whole.jobId}
              onChange={(jobId) =>
                setWhole({ jobId, itemId: proposalFor(data, jobId, data.taxable) })
              }
            />
            <ItemSelect
              data={data}
              jobId={whole.jobId}
              value={whole.itemId}
              onChange={(itemId) => setWhole({ ...whole, itemId })}
            />
            <button type="button" disabled={!whole.jobId} onClick={applyWhole}>
              Applica a tutte le righe
            </button>
          </div>
        )}

        {byLine ? (
          <div className="allocation-table">
            <table className="lines allocation-lines">
              <thead>
                <tr>
                  <th>Riga della fattura</th>
                  <th className="right">Importo</th>
                  <th>Commessa o progetto</th>
                  <th>{issued ? 'Rata' : 'Costo'}</th>
                  <th className="right">Imputato</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.lines.map((line) => {
                  const mine = lineParts(line.position);
                  // «Dividi» once the line has its commessa.
                  const canAdd = mine.every((item) => item.jobId);
                  const span = mine.length + (canAdd ? 1 : 0);
                  return (
                    <Fragment key={line.position}>
                      {mine.map((item, index) => (
                        <tr key={item.key} className={index > 0 ? 'allocation-split' : undefined}>
                          {index === 0 ? (
                            <>
                              <td rowSpan={span}>
                                <span className="muted">{line.position + 1}.</span>{' '}
                                {line.description}
                              </td>
                              <td rowSpan={span} className="right">
                                {formatMoney(line.amount)}
                                <LineRest line={line} />
                              </td>
                            </>
                          ) : null}
                          <PartCells
                            data={data}
                            item={item}
                            onChange={(change) => update(item.key, change)}
                            onRemove={mine.length > 1 ? () => remove(item.key) : undefined}
                          />
                        </tr>
                      ))}
                      {canAdd && (
                        <tr className="allocation-split">
                          {mine.length === 0 && (
                            <>
                              <td>
                                <span className="muted">{line.position + 1}.</span>{' '}
                                {line.description}
                              </td>
                              <td className="right">
                                {formatMoney(line.amount)}
                                <LineRest line={line} />
                              </td>
                            </>
                          )}
                          <td colSpan={4}>
                            <button
                              type="button"
                              className="link"
                              onClick={() => addTo(line.position)}
                            >
                              {mine.length ? 'Dividi: + un’altra commessa' : 'Imputa la riga'}
                            </button>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {(!byLine || wholeParts.length > 0) && (
          <div className="stack">
            {byLine && (
              <p className="muted small">
                Parti dell’intera fattura, imputate prima della ripartizione per riga: puoi
                lasciarle o toglierle e imputare le righe.
              </p>
            )}
            {wholeParts.map((item) => (
              <div key={item.key} className="allocation-row">
                <PartCells
                  data={data}
                  item={item}
                  onChange={(change) => update(item.key, change)}
                  onRemove={() => remove(item.key)}
                  asCells={false}
                />
              </div>
            ))}
            {!byLine && (
              <button type="button" className="link" onClick={() => addTo(null)}>
                + Un’altra commessa
              </button>
            )}
          </div>
        )}

        {lostParts.length > 0 && (
          <div className="notice info stack">
            <span>
              Queste parti vengono da righe che Gestify non ha più: toglile e imputa le righe di
              adesso.
            </span>
            {lostParts.map((item) => (
              <div key={item.key} className="row between">
                <span>
                  riga {item.linePosition! + 1} ·{' '}
                  {data.candidates.find((job) => job.id === item.jobId)?.code ?? 'commessa'} ·{' '}
                  {formatMoney(moneyOf(item.amount) ?? '0')}
                </span>
                <button type="button" className="link" onClick={() => remove(item.key)}>
                  Togli
                </button>
              </div>
            ))}
          </div>
        )}

        {withoutRataJobs.length > 0 && (
          <div className="notice info stack">
            <span>
              {withoutRataJobs.join(', ')} {withoutRataJobs.length === 1 ? 'ha' : 'hanno'} ancora
              rate da fatturare: se imputi senza rata, restano da fatturare anche se la fattura le
              copre.
            </span>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={withoutRata}
                onChange={(event) => setWithoutRata(event.target.checked)}
              />{' '}
              Imputo senza rata lo stesso
            </label>
          </div>
        )}
        {given.some(costMissing) && (
          <p className="notice info">
            La commessa ha dei costi: scegli quello di cui la fattura fa parte, oppure «Nessun
            costo» se è una spesa non prevista. Senza, il budget la conta in più rispetto ai costi.
          </p>
        )}
        {problem ? (
          <p className="error">{problem}</p>
        ) : (
          <p className={free === 0 ? 'muted small' : 'notice info'}>
            Imputato {formatMoney(fromCents(total))} di {formatMoney(data.taxable)}
            {free === 0
              ? '.'
              : `: restano da imputare ${formatMoney(fromCents(free))}. Puoi salvare lo stesso.`}
          </p>
        )}
        {save.isError && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!ready || save.isPending}
            onClick={() =>
              save.mutate({
                allocations: given.map((item) => ({
                  jobId: item.jobId,
                  installmentId: issued ? item.itemId || null : null,
                  costId: issued || item.itemId === NO_COST ? null : item.itemId || null,
                  linePosition: item.linePosition,
                  amount: amounts.get(item.key)!,
                })),
              })
            }
          >
            {save.isPending ? 'Attendere…' : 'Salva'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/** Commessa, rata or cost, amount and «Togli» of one part: table cells, or a grid row. */
function PartCells({
  data,
  item,
  onChange,
  onRemove,
  asCells = true,
}: {
  data: Data;
  item: Part;
  onChange: (change: Partial<Part>) => void;
  onRemove?: () => void;
  asCells?: boolean;
}) {
  const Cell = asCells ? 'td' : Fragment;
  return (
    <>
      <Cell>
        <JobSelect
          data={data}
          value={item.jobId}
          onChange={(jobId) => onChange({ jobId, itemId: '' })}
          emptyLabel={item.linePosition === null ? 'Scegli…' : 'Non imputata'}
        />
      </Cell>
      <Cell>
        <ItemSelect
          data={data}
          jobId={item.jobId}
          value={item.itemId}
          onChange={(itemId) => onChange({ itemId })}
        />
      </Cell>
      <Cell>
        {item.jobId ? (
          <DecimalInput
            value={item.amount}
            onChange={(amount) => onChange({ amount })}
            decimals={[2, 2]}
            aria-label="Imponibile"
          />
        ) : (
          <span className="muted">—</span>
        )}
      </Cell>
      <Cell>
        {onRemove && (
          <button type="button" className="link" onClick={onRemove}>
            Togli
          </button>
        )}
      </Cell>
    </>
  );
}

/** The commesse and progetti, those of the subject first (2.44: progetti apart). */
function JobSelect({
  data,
  value,
  onChange,
  emptyLabel = 'Scegli…',
}: {
  data: Data;
  value: string;
  onChange: (jobId: string) => void;
  emptyLabel?: string;
}) {
  const issued = data.direction === 'emessa';
  const same = data.candidates.filter((job) => job.sameSubject);
  const others = data.candidates.filter((job) => !job.sameSubject && job.kind === 'commessa');
  const projects = data.candidates.filter((job) => !job.sameSubject && job.kind === 'progetto');
  const group = (label: string, jobs: Data['candidates']) =>
    jobs.length > 0 && (
      <optgroup label={label}>
        {jobs.map((job) => (
          <option key={job.id} value={job.id}>
            {job.code} · {job.name}
          </option>
        ))}
      </optgroup>
    );
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label="Commessa o progetto"
    >
      <option value="">{emptyLabel}</option>
      {group(issued ? 'Del cliente o di «Si fattura a»' : 'Con costi di questo fornitore', same)}
      {group('Altre commesse', others)}
      {group('Progetti', projects)}
    </select>
  );
}

/**
 * The rate (issued) or costs (received) of a commessa, with what was invoiced
 * on them by the other invoices: this one's saved parts are taken out, they are
 * being chosen again.
 */
function itemsOf(data: Data, jobId: string) {
  const job = data.candidates.find((item) => item.id === jobId);
  if (!job) return [];
  const issued = data.direction === 'emessa';
  const items = issued ? job.installments : job.costs;
  return items.map((item) => {
    const mine = data.allocations
      .filter((row) => (issued ? row.installmentId : row.costId) === item.id)
      .reduce((sum, row) => sum + toCents(row.amount), 0);
    return { ...item, invoiced: fromCents(toCents(item.invoiced) - mine) };
  });
}

/** Those still to invoice, in part or in whole, also counting the parts being chosen. */
function openItems(
  data: Data,
  jobId: string,
  parts: readonly Pick<Part, 'jobId' | 'itemId' | 'amount'>[] = [],
) {
  return withParts(data, jobId, parts).filter(
    (item) => toCents(item.invoiced) !== toCents(item.amount),
  );
}

/** The items of a commessa with the parts being chosen counted as invoiced on them. */
function withParts(
  data: Data,
  jobId: string,
  parts: readonly Pick<Part, 'jobId' | 'itemId' | 'amount'>[],
) {
  return itemsOf(data, jobId).map((item) => {
    const taken = parts
      .filter((other) => other.jobId === jobId && other.itemId === item.id)
      .reduce((sum, other) => sum + toCents(moneyOf(other.amount) ?? '0'), 0);
    return { ...item, invoiced: fromCents(toCents(item.invoiced) + taken) };
  });
}

/** The rata or cost proposed for a part (2.47): none, «senza», when all are invoiced. */
function proposalFor(
  data: Data,
  jobId: string,
  amount: string,
  others: readonly Pick<Part, 'jobId' | 'itemId' | 'amount'>[] = [],
): string {
  return proposedItem(withParts(data, jobId, others), amount) ?? '';
}

function jobCode(data: Data, jobId: string): string {
  return data.candidates.find((job) => job.id === jobId)?.code ?? 'La commessa';
}

/** The rata of an issued invoice, the cost of a received one. */
function ItemSelect({
  data,
  jobId,
  value,
  onChange,
}: {
  data: Data;
  jobId: string;
  value: string;
  onChange: (itemId: string) => void;
}) {
  const issued = data.direction === 'emessa';
  const job = data.candidates.find((item) => item.id === jobId);
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={issued ? 'Rata' : 'Costo'}
      disabled={!job}
    >
      {issued || !job?.costs.length ? (
        <option value="">{issued ? 'Senza rata' : 'Senza costo'}</option>
      ) : (
        <>
          <option value="">Scegli il costo…</option>
          <option value={NO_COST}>Nessun costo (spesa non prevista)</option>
        </>
      )}
      {itemsOf(data, jobId).map((item) => (
        <option key={item.id} value={item.id}>
          {item.description} · {formatMoney(item.amount)}
          {toCents(item.invoiced) !== 0 && ` (già ${formatMoney(item.invoiced)})`}
        </option>
      ))}
    </select>
  );
}
